import { useCallback, useEffect, useRef } from 'react'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { getStaleStateReason } from '@/utils/store.utils'
import { logger } from '@/utils/logger'

/**
 * Resets the store when the user returns to a tab holding a selected date or timeslot
 * that has since elapsed — e.g. a tab left open overnight still pointing at yesterday.
 *
 * Clearing `activeDate` is all that is needed to re-select a date: `TimeSelectCalendar`
 * picks the earliest available date whenever there is none.
 *
 * Two triggers, because neither covers the other:
 * - `siteIsVisible` flipping false -> true, i.e. `visibilitychange`. The reliable one:
 *   tab switches, minimise, mobile backgrounding.
 * - window `focus` / `pageshow`. Covers alt-tabbing back from another application with
 *   the tab still in the foreground, which fires no `visibilitychange`, and back/ forward cache
 *   restores. Noisier — `focus` also fires on closing devtools or dismissing an iframe,
 *   which is why TanStack Query dropped it from its own focusManager. Harmless here:
 *   unlike a refetch, a spurious fire costs nothing because the check only resets when
 *   state is genuinely stale.
 */
const useStaleStateReset = (): void => {
  const { timezone } = useSeller()
  const siteIsVisible = useOurStore((state) => state.siteIsVisible)
  const resetAll = useOurStore((state) => state.resetAll)
  const activeDate = useOurStore((state) => state.activeDate)
  const activeStartTime = useOurStore((state) => state.activeStartTime)
  const wasVisible = useRef(siteIsVisible)

  const resetIfStale = useCallback((): void => {
    const staleReason = getStaleStateReason({
      activeDate,
      activeStartTimeEndTime: activeStartTime.endTime,
      timezone,
    })

    if (!staleReason) return

    logger().log(`[useStaleStateReset] ${staleReason}`)
    resetAll()
  }, [activeDate, activeStartTime, timezone, resetAll])

  // Tab became visible again after being hidden
  useEffect(() => {
    const becameVisible = siteIsVisible && !wasVisible.current
    wasVisible.current = siteIsVisible

    if (!becameVisible) return

    resetIfStale()
  }, [siteIsVisible, resetIfStale])

  // Window regained focus, or the page was restored from the back/forward cache
  useEffect(() => {
    window.addEventListener('focus', resetIfStale)
    window.addEventListener('pageshow', resetIfStale)

    return () => {
      window.removeEventListener('focus', resetIfStale)
      window.removeEventListener('pageshow', resetIfStale)
    }
  }, [resetIfStale])
}

export default useStaleStateReset
